import { Component, OnInit, ViewChild } from '@angular/core'
import { lastValueFrom } from 'rxjs'
import { MicomelUserAndStatusUi } from 'src/api/micomel-migrations-api/micomel-migrations-api.model'
import { ShimuraGarageMigrationsService } from 'src/api/micomel-migrations-api/micomel-migrations-api.service'
import { ProScheApplicationService } from 'src/api/temporary-apis/prosche-application.service'
import { ProScheContractCompanyListResult } from 'src/api/temporary-apis/prosche-application.interface'
import { sortedItems } from 'src/app/common/common'
import { MessageService } from 'src/app/components/message/message.service'
import { GridActionButton, ActionTargetInfo } from 'src/app/module/is-grid/action-grid/action-grid.component.interface'
import { Header, RowSelection } from 'src/app/module/is-grid/base-grid/base-grid.component.interface'
import { environment } from 'src/environments/environment'
import { UraProscheApplicationDetailModalComponent } from '../ura-prosche-application-detail-modal/ura-prosche-application-detail-modal.component'

interface ProScheNoticeSetting {
  testMode?: boolean
}

@Component({
  selector: 'app-ura-migration-send-list',
  templateUrl: './ura-migration-send-list.component.html'
})
export class UraMigrationSendListComponent implements OnInit {
  title = 'ProSche無償利用開始のお申込み状況'
  data: MicomelUserAndStatusUi[] = []
  columns: Header[] = [
    {
      key: 'companyOrganizationCd',
      label: '',
      hidden: true
    },
    {
      key: 'companyOrganizationId',
      label: '',
      hidden: true
    },
    {
      key: 'customerCd',
      label: '',
      hidden: true
    },
    {
      key: 'parentContractId',
      label: '',
      hidden: true
    },
    {
      key: 'parentActuateId',
      label: '',
      hidden: true
    },
    {
      key: 'contractCompanyName',
      label: '契約企業',
      width: '17%'
    },
    {
      key: 'number',
      label: '利用明細No.',
      width: '8%'
    },
    {
      key: 'purchaserName',
      label: '契約者',
      width: '8%'
    },
    // {
    //   key: 'hasBefore',
    //   label: '解約有',
    //   dataType: 'check',
    //   width: '7%'
    // },
    // {
    //   key: 'beforeTotalAmount',
    //   label: '解約',
    //   dataType: 'number',
    //   width: '8%'
    // },
    {
      key: 'afterTotalAmount',
      label: '新規契約',
      dataType: 'number',
      width: '9%'
    },
    // {
    //   key: 'totalAmount',
    //   label: '差額',
    //   dataType: 'number',
    //   width: '8%'
    // },
    {
      key: 'sendMailDatetimeUi',
      label: 'メール送信日時',
      width: '10%',
      style: {
        textAlign: 'center'
      }
    },
    {
      key: 'approvalDatetimeUi',
      label: '継続利用申込み日時',
      width: '10%',
      style: {
        textAlign: 'center'
      }
    },
    {
      key: 'mailAddress',
      label: 'メールアドレス'
    },
    {
      key: 'stockMigrationUserAndStatusId',
      hidden: true,
      label: '',
      width: ''
    }
  ]
  rowSelection = RowSelection.MULTIPLE
  cellClasses = {}
  actionButtons: GridActionButton[] = []
  loading = true
  sending = false
  isTestMode = false

  @ViewChild(UraProscheApplicationDetailModalComponent, { static: false })
  detailModal: UraProscheApplicationDetailModalComponent

  constructor(private messageService: MessageService, private proScheApplicationService: ProScheApplicationService) {}

  async ngOnInit(): Promise<void> {
    this.isTestMode = await this.getProScheTestMode()
    this.setActionButtons()
    this.getContractCompanyList()
  }

  private setActionButtons(): void {
    this.actionButtons = [
      {
        label: '御見積書確認',
        actionCd: 'confirm'
      },
      {
        label: 'メールを送信',
        actionCd: 'send',
        successMessage: true,
        confirmDialog: true
      }
    ]

    if (this.isTestMode) {
      this.actionButtons.push({
        label: 'テスト用 申込キャンセル',
        actionCd: 'testCancel',
        confirmDialog: true
      })
    }
  }

  private async getProScheTestMode(): Promise<boolean> {
    const setting = await this.getProScheNoticeSettingJson()
    return setting?.testMode === true
  }

  private async getProScheNoticeSettingJson(): Promise<ProScheNoticeSetting> {
    try {
      const promis = await fetch(
        `${environment.indexCoreMyideaUrl}/api/fileSystem?fileSystemId=notification%2FproscheNoticeSetting.json`,
        {
          cache: 'no-store'
        }
      )
      const json = await promis.json()
      return json
    } catch {
      return null
    }
  }

  async getContractCompanyList(): Promise<void> {
    this.data = []
    this.loading = true
    const ret$ = await this.proScheApplicationService.getProScheApplicationsContractCompanyList()
    let ret = await lastValueFrom(ret$)
    this.loading = false
    if (ret?.resultCode !== 0) {
      return
    }
    this.data = sortedItems(ret.data, 'contractCompanyName')
    this.data.forEach((item) => {
      item.actionCds = ['confirm']
      if (this.isTestMode) {
        item.actionCds.push('testCancel')
      }
      if (item.approvalDatetime === null) {
        item.actionCds.push('send')
      }
    })
  }

  async send(companyOrganizationCds: string[]): Promise<void> {
    this.data = []
    this.sending = true
    const ret$ = await this.proScheApplicationService.postProScheApplicationSendMail(companyOrganizationCds)
    let ret = await lastValueFrom(ret$)
    this.sending = false
    if (ret?.resultCode !== 0) {
      return
    }
    this.messageService.success('ProSche無償利用開始のお申込みを送信しました')
    this.getContractCompanyList()
  }

  async testCancel(stockMigrationUserAndStatusIds: string[]): Promise<void> {
    this.sending = true
    const ret$ = await this.proScheApplicationService.testCancel(stockMigrationUserAndStatusIds)
    const ret = await lastValueFrom(ret$)
    this.sending = false
    if (ret?.resultCode !== 0) {
      this.messageService.error('テスト用 申込キャンセルの実行に失敗しました')
      return
    }
    this.messageService.success('テスト用 申込キャンセルを実行しました')
    this.getContractCompanyList()
  }

  actionCommit(event: ActionTargetInfo): void {
    if (this.sending) {
      return
    }
    const stockMigrationUserAndStatusId = []
    event.rows.forEach((row) => {
      stockMigrationUserAndStatusId.push(row.stockMigrationUserAndStatusId)
    })
    if (event.actionCd === 'confirm') {
      // 御見積書確認：モーダルを開く
      if (this.detailModal) {
        // 型アサーション：実際のデータはProScheContractCompanyListResult型
        this.detailModal.open(event.rows as ProScheContractCompanyListResult[])
      }
    } else if (event.actionCd === 'send') {
      this.send(stockMigrationUserAndStatusId)
    } else if (event.actionCd === 'testCancel') {
      this.testCancel(stockMigrationUserAndStatusId)
    }
  }
}
